import * as React from "react";
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import BookIcon from "@mui/icons-material/Book";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import DefaultIcon from "@mui/icons-material/ViewList";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Store";
import { useLocation } from "react-router-dom";
import { DashboardMenuItem, Menu, MenuItemLink, useResourceDefinitions, useSidebarState } from "react-admin";

export const SidebarMenu = (props) => {
  const resourcesDefinitions = useResourceDefinitions();
  const resources = Object.keys(resourcesDefinitions).map((name) => resourcesDefinitions[name]);
  const [open] = useSidebarState();
  const location = useLocation();

  const renderResourceLink = (resourceName) => {
    const resource = resourcesDefinitions[resourceName];
    if (!resource || !resource.hasList) {
      return null;
    }
    const Icon = resource.icon;
    return (
      <MenuItemLink
        key={resource.name}
        to={`/${resource.name}`}
        primaryText={(resource.options && resource.options.label) || resource.name}
        leftIcon={Icon ? <Icon /> : <DefaultIcon />}
        onClick={props.onMenuClick}
        sidebarIsOpen={open}
        sx={{
          "& .MuiListItemText-primary": {
            fontSize: "0.8em",
          },
        }}
      />
    );
  };

  const groupedResourceNames = new Set([
    "orders",
    "shipping",
    "invoices-archive",
    "v2Products",
    "v2Productpres",
    "v2Bundles",
    "v2Bundlepres",
    "productTags",
    "prices",
    "pdpGroup",
    "promotionCode",
    "cancellationCoupon",
    "appPuzzle",
    "appRedirectLink",
    "geoffreyProductRecommendations",
    "emailProductDetailText",
    "emailTemplates",
    "firebaseNotificationMessage",
    "users",
    "subscriptions",
    "freeSubscriptions",
    "internalUsers",
    "internalUserGroups",
    "featureFlags",
    "locales",
    "translationKey",
  ]);

  const MenuGroup = ({ label, icon: Icon, items, extraPaths = [], customLinks = [] }) => {
    const resourcePaths = items
      .map((name) => (resourcesDefinitions[name] ? `/${name}` : null))
      .filter(Boolean);
    const isActive = [...resourcePaths, ...extraPaths].some((path) => location.pathname.startsWith(path));
    const defaultOpen = label === "Admin" ? isActive : true;
    const storageKey = `pb-admin:menu-group:${label}`;
    const getStoredState = () => {
      try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored === "open") return true;
        if (stored === "closed") return false;
      } catch (error) {
        // ignore storage errors and fall back to defaults
      }
      return defaultOpen;
    };
    const [groupOpen, setGroupOpen] = React.useState(getStoredState);

    React.useEffect(() => {
      if (isActive) {
        setGroupOpen(true);
        return;
      }
      setGroupOpen(getStoredState());
    }, [isActive, storageKey]);

    const handleToggle = () => {
      setGroupOpen((prev) => {
        const next = !prev;
        try {
          window.localStorage.setItem(storageKey, next ? "open" : "closed");
        } catch (error) {
          // ignore storage errors
        }
        return next;
      });
    };

    return (
      <>
        <ListItemButton
          onClick={handleToggle}
          sx={{ px: 2, py: 0.5 }}
          disabled={!open}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>{Icon ? <Icon /> : <DefaultIcon />}</ListItemIcon>
          {open && <ListItemText primary={label} sx={{ "& .MuiListItemText-primary": { fontSize: "0.8em" } }} />}
          {open && (groupOpen ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
        <Collapse in={groupOpen && open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {items.map((resourceName) => renderResourceLink(resourceName))}
            {customLinks.map((link) => (
              <MenuItemLink
                key={link.to}
                to={link.to}
                primaryText={link.label}
                leftIcon={link.icon}
                onClick={props.onMenuClick}
                sidebarIsOpen={open}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.8em",
                  },
                }}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  };

  return (
    <Menu
      {...props}
      sx={{
        "& .MuiListItemText-primary": {
          fontSize: "0.8em",
        },
        "& .RaMenuItemLink-root .MuiListItemText-primary": {
          fontSize: "0.8em",
        },
        "& .RaMenuItemLink-root .MuiTypography-root": {
          fontSize: "0.8em",
        },
        "& .RaMenuItemLink-primaryText": {
          fontSize: "0.8em",
        },
        "& .RaMenuItemLink-root .RaMenuItemLink-primaryText": {
          fontSize: "0.8em !important",
        },
      }}
    >
      {/* <DashboardMenuItem primaryText="Dashboard" /> */}

      <MenuGroup
        label="Orders"
        icon={StoreIcon}
        items={["orders", "shipping", "orderTesters", "invoices-archive"]}
        customLinks={[{ to: "/order_charts", label: "Order Charts", icon: <BarChartIcon /> }]}
      />
      <MenuGroup
        label="Product Catalogue"
        icon={BookIcon}
        items={["v2Products", "v2Productpres", "v2Bundles", "v2Bundlepres", "productTags", "prices", "pdpGroup"]}
      />
      <MenuGroup label="Gamification" items={["challenge", "journey"]}/>
      <MenuGroup
        label="Marketing"
        icon={ChatBubbleIcon}
        items={[
          "promotionCode",
          "cancellationCoupon",
          "appPuzzle",
          "appRedirectLink",
          "geoffreyProductRecommendations",
          "geoffreyRecommendationTexts",
          "emailProductDetailText",
          "emailTemplates",
          "firebaseNotificationMessage",
        ]}
        customLinks={[
          { to: "/notification-settings", label: "Firebase Notification Settings", icon: <SettingsIcon /> },
          { to: "/sunstar-redirect-urls", label: "App Deep Links", icon: <LinkIcon /> },
        ]}
      />
      <MenuGroup
        label="Customers"
        icon={PeopleIcon}
        items={["users", "subscriptions", "freeSubscriptions"]}
      />
      <MenuGroup
        label="Admin"
        icon={SettingsIcon}
        items={["internalUsers", "internalUserGroups", "featureFlags", "locales"]}
        customLinks={[{ to: "/settings", label: "Settings", icon: <SettingsIcon /> }]}
      />
      <MenuGroup label="Mouthcheck" icon={SettingsIcon} items={["translationKeys", "mouthscanAnalysisResults"]} />

      <List component="div" disablePadding sx={{ mt: 1 }}>
        <MenuItemLink
          to="/basket"
          primaryText="Store"
          leftIcon={<StoreIcon />}
          onClick={props.onMenuClick}
          sidebarIsOpen={open}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "0.8em",
            },
          }}
        />
      </List>
    </Menu>
  );
};
